import { useFieldArray, useWatch } from "react-hook-form";
import type { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { sumParcelWeight, formatParcelWeight } from "./shared/parcelTotals";
import {
  ParcelEditRow,
  PARCEL_GRID_COLUMNS,
  PARCEL_LIST_MAX_H,
  parcelCellCls,
} from "./shared/ParcelEditRow";

/**
 * Shape of the parcels-related fields the editor reads from the parent form.
 * The parent {@link CreateOrderModal} form values must contain these keys.
 */
export interface ParcelFormFields {
  parcels?: { code?: string; description?: string; weight_kg?: string }[];
}

interface Props<TFieldValues extends ParcelFormFields> {
  /** Parent react-hook-form control. */
  control: Control<TFieldValues>;
  /** Parent react-hook-form register. */
  register: UseFormRegister<TFieldValues>;
  /** Parent form errors (used to surface nested parcel-code errors). */
  errors: FieldErrors<TFieldValues>;
}

/*
  The row geometry and the row LAYOUT live in `shared/ParcelEditRow`, so the
  detail view's table is the same component rather than a second copy of this
  markup (owner, 2026-08-25: one interaction model for one action).

  It is a FIXED height, not a max (owner, 2026-08-25: «сделай область посылок
  как раз на размер 4х шт»). As a max, the viewport still tracked its content
  below four rows, so the whole block — and with it the column's bottom edge —
  jumped by 42px every time a row was added or removed. Pinning the height makes
  the parcels block a constant in the dialog's height budget: the only thing that
  changes as rows come and go is how far the list scrolls inside it.
*/
const headCls =
  "text-[10px] font-semibold uppercase tracking-wider text-gray-400";

/**
 * Parcel entry block for the create-order form — the mockup's TABLE
 * (ШТРИХКОД / ОПИСАНИЕ / ВЕС + per-row trash), not a card list.
 *
 * It does not own a form — it operates on the parent form's `control` /
 * `register`, so the rows submit together with the rest of the order payload.
 *
 * The block also reports the running total («4 места · 12 кг»), which is the
 * same number the parent uses as the order's computed weight. It is derived
 * from one helper, {@link sumParcelWeight}, so the footer and the «Вес, кг»
 * field can never disagree.
 */
export function ParcelRowsEditor<TFieldValues extends ParcelFormFields>({
  control,
  register,
  errors,
}: Props<TFieldValues>) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    // The field array path is constant; cast keeps the generic call type-safe.
    name: "parcels" as never,
  });

  // Live values — `fields` holds the ORIGINAL append-time values and never
  // updates as the user types, so summing it would freeze the total at 0.
  const rows = (useWatch({ control, name: "parcels" as never }) ??
    []) as ParcelFormFields["parcels"];
  const totalWeight = sumParcelWeight(rows);

  return (
    <section
      aria-labelledby="create-order-parcels-heading"
      className="space-y-2 rounded-xl border border-gray-200 p-3"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h4
          id="create-order-parcels-heading"
          className="min-w-0 text-[11px] font-semibold uppercase tracking-widest text-gray-400"
        >
          {t("orders.create.parcels.title")}{" "}
          {/* The count is BRAND-coloured because it is the block's live
              subject, not a status: it changes as the dispatcher works.
              `brand-primary` is the dealer's runtime token (§10.10). */}
          <span className="font-semibold normal-case tracking-normal text-brand-primary">
            {t("orders.create.parcels.placeCount", { count: fields.length })}
          </span>
        </h4>
        {/*
          Shown only while the weight IS the sum. With no weighed rows the
          «Вес, кг» field is an ordinary editable input (owner, 2026-08-25), and
          this caption would be describing a rule that is not in force —
          claiming the dispatcher's hand-typed weight came from a parcel list.
        */}
        {totalWeight > 0 && (
          <span className="shrink-0 text-[11px] text-gray-400">
            {t("orders.create.parcels.weightIsSum")}
          </span>
        )}
      </div>

      <div
        className="grid gap-1.5 px-0.5"
        style={{ gridTemplateColumns: PARCEL_GRID_COLUMNS }}
      >
        <span className={headCls}>{t("orders.create.parcels.code")}</span>
        <span className={headCls}>
          {t("orders.create.parcels.descriptionLabel")}
        </span>
        <span className={headCls}>{t("orders.create.parcels.weight")}</span>
        <span />
      </div>

      {/*
        THE dialog's only scroll container (owner ruling), and the one block in
        it with a FIXED height rather than a content-driven one: exactly four
        rows, always. A 40-parcel order scrolls here and nowhere else, and a
        1-parcel order reserves the same box instead of collapsing — so adding
        or removing a row never moves the column's bottom edge, which is what
        lets the three columns stay aligned (owner, 2026-08-25).

        Rendered unconditionally, header included: gating it on
        `fields.length > 0` is what made the block collapse at zero rows and
        reintroduces exactly the height jump this fixes.
      */}
      <div
        data-testid="parcel-rows-scroll"
        className="space-y-1.5 overflow-y-auto overflow-x-hidden"
        style={{ height: `${PARCEL_LIST_MAX_H}px` }}
      >
        {fields.length === 0 ? (
          <p className="flex h-full items-center justify-center px-2 text-center text-xs text-gray-400">
            {t("orders.create.parcels.emptyHint")}
          </p>
        ) : (
          fields.map((field, index) => {
            // Surface a per-row code error returned inline by the parent form.
            const parcelErrors = (errors as FieldErrors<ParcelFormFields>)
              .parcels?.[index];
            const codeError = parcelErrors?.code?.message;
            // Each row's controls are named by their POSITION, so no two
            // inputs in the dialog share an accessible name (BUG-504/508).
            const rowNo = index + 1;
            return (
              <ParcelEditRow
                key={field.id}
                error={codeError}
                code={
                  <input
                    aria-label={t("orders.create.parcels.codeRow", {
                      row: rowNo,
                    })}
                    className={parcelCellCls}
                    {...register(`parcels.${index}.code` as never)}
                  />
                }
                description={
                  <input
                    aria-label={t("orders.create.parcels.descriptionRow", {
                      row: rowNo,
                    })}
                    placeholder={t(
                      "orders.create.parcels.descriptionPlaceholder",
                    )}
                    className={parcelCellCls}
                    {...register(`parcels.${index}.description` as never)}
                  />
                }
                weight={
                  <input
                    aria-label={t("orders.create.parcels.weightRow", {
                      row: rowNo,
                    })}
                    type="number"
                    min="0"
                    step="0.001"
                    className={`${parcelCellCls} px-2 tabular-nums`}
                    {...register(`parcels.${index}.weight_kg` as never)}
                  />
                }
                action={
                  <button
                    type="button"
                    aria-label={t("orders.create.parcels.removeRowNo", {
                      row: rowNo,
                    })}
                    onClick={() => remove(index)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-brand-danger/10 hover:text-brand-danger"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                }
              />
            );
          })
        )}
      </div>

      {/* Footer row — «+ Место» left, «4 места · 12 кг» right. */}
      <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2">
        <button
          type="button"
          onClick={() =>
            append({ code: "", description: "", weight_kg: "" } as never)
          }
          className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
        >
          {t("orders.create.parcels.addRow")}
        </button>
        <span className="shrink-0 text-xs font-semibold text-gray-700 tabular-nums">
          {t("orders.create.parcels.totals", {
            count: fields.length,
            weight: formatParcelWeight(totalWeight),
          })}
        </span>
      </div>
    </section>
  );
}
